import { createRouter, createWebHistory, isNavigationFailure } from "vue-router";

import middlewarePipeline from "@/app/router/middlewares/pipeline";
import { checkVersionAndRefresh } from "@/composables/useVersionCheck";
import i18n, { loadLocaleMessages } from "@/i18n";
import store from "@/store";

import routes from "./routes";

const router = createRouter({
  history: createWebHistory(),
  strict: true,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      window.scrollTo(savedPosition.left, savedPosition.top);
    }
  },
});

router.beforeEach(async (to, from, next) => {
  // Wait for app initialization (user fetch) before any route guards run
  await store.dispatch("initializeApp");

  // Re-fetch licence/usage data if stale to prevent showing outdated licence status
  if (store.getters.isAuth) {
    store.dispatch("refreshLicenceIfStale");
    store.dispatch("refreshUsageIfStale");
  }

  // Auto-refresh if a new version has been deployed
  checkVersionAndRefresh(to.fullPath);

  // Handle language from query parameter
  const langParam = to.query.lang;

  if (langParam) {
    await loadLocaleMessages(langParam);
    i18n.global.locale.value = langParam;
    localStorage.setItem("locale", langParam);
  }

  const nonRedirectUrls = [
    "common.login",
    "common.register",
    "common.password.recovery",
    "common.confirm.registration",
    "common.create.new.password",
    "common.supported.platforms.list",
  ];

  if (store.getters.isAuth && nonRedirectUrls.includes(to.name)) {
    return next({ name: "dashboard.home" });
  }

  if (!store.getters.isAuth && !nonRedirectUrls.includes(to.name)) {
    return next({ name: "common.login", query: { redirect: to.fullPath } });
  }

  if (!to.meta.middleware) {
    return next();
  }

  if (store.getters.isAuth) {
    return next();
  }

  const { middleware } = to.meta;
  const context = {
    to,
    from,
    next,
    store,
  };

  return middleware[0]({
    ...context,
    nextMiddleware: middlewarePipeline(context, middleware, 1),
  });
});

router.onError((error) => {
  console.error("Router navigation error:", error);

  if (isNavigationFailure(error)) {
    console.warn("Navigation failed. Redirecting to dashboard home.");
    if (router.currentRoute.value?.name !== "dashboard.home") {
      router.push({ name: "dashboard.home" }).catch((err) => {
        console.error("Failed to redirect to dashboard home:", err);
      });
    }
  }
});

export default router;
